import { selectedFeatureAtom } from '@/atoms';
import { ProjectShareDialogContent } from '@/components';
import FullPageError from '@/components/FullPageError';
import { Button } from '@/components/ui/button';
import { ViewerSettingsDialog } from '@/components/ViewerSettingsDialog';
import { Project } from '@/graphql/graphql';
import { useOrganizations } from '@/hooks/useOrganizations';
import PageSkeleton, { iAction } from '@/layout/Skeleton';
import { useDialog } from '@/providers/DialogProvider';
import { CogFourSolid, DangerTriangleSolid, Edit, LayersThree, SaveSolid, Share, Upload } from '@mynaui/icons-react';
import { useAtom } from 'jotai';
import posthog from 'posthog-js';
import { FunctionComponent, lazy, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate, useOutletContext, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
const Viewer = lazy(() => import('@/components/Viewer'));

const checkWebGLSupport = () => {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      !!window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch (e) {
    return false;
  }
};

interface iViewerProps {}

export const useViewerActions = (projectId: string) => {
  const navigate = useNavigate();
  const { organization, hasRole } = useOrganizations();
  const { openDialog } = useDialog();
  const [params, setParams] = useSearchParams();
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const isMeasurementsEnabled = params.get('showMeasurements') === 'true';
  const isLayersEnabled = params.get('layers') === 'true';

  const _handleToggleLayers = () => {
    const params = new URLSearchParams(window.location.search);
    params.set('layers', isLayersEnabled ? 'false' : 'true');
    posthog.capture(isLayersEnabled ? 'Project Layers Disabled' : 'Project Layers Enabled');
    setParams(params);
  };

  const _handleUploadData = () => {
    if (!organization) throw new Error('Organization is not defined');
    posthog.capture('Project Upload Opened');
    navigate(`/projects/${projectId}/upload`);
  };

  const _handleEditProject = () => {
    if (!organization) throw new Error('Organization is not defined');
    posthog.capture('Project Edit Opened');
    navigate(`/projects/${projectId}/edit`);
  };

  const _handleOpenSettings = () => {
    posthog.capture('Project Settings Opened');
    setSettingsDialogOpen(true);
  };

  const _handleOpenShare = () => {
    posthog.capture('Project Share Opened');
    openDialog(<ProjectShareDialogContent projectId={projectId!} />, 'sm:max-w-[800px]');
  };

  const actions: (iAction | null)[] = [
    {
      icon: <LayersThree />,
      onClick: _handleToggleLayers,
      tooltip: 'Toggle layers',
      appearance: isLayersEnabled ? 'primary' : 'secondary',
    },
    {
      icon: <CogFourSolid />,
      onClick: _handleOpenSettings,
      tooltip: 'Viewer Settings',
      appearance: 'secondary',
    },
  ];

  if (hasRole('EDITOR')) {
    actions.unshift(
      {
        icon: <Upload />,
        onClick: _handleUploadData,
        tooltip: 'Data Center',
      },
      null,
    );
  }
  if (hasRole('EDITOR')) {
    actions.unshift({
      icon: <Edit />,
      onClick: _handleEditProject,
      tooltip: 'Edit Project',
    });
  }
  if (hasRole('ADMIN')) {
    actions.unshift({
      icon: <Share />,
      onClick: _handleOpenShare,
      tooltip: 'Share Project',
    });
  }

  return { actions, isSettingsDialogOpen: settingsDialogOpen, setSettingsDialogOpen };
};

const ViewerPage: FunctionComponent<iViewerProps> = () => {
  const { projectId } = useParams();
  const isWebGLSupported = useMemo(() => checkWebGLSupport(), []);

  const { project, projectLoading } = useOutletContext<{
    project: Project;
    projectLoading: boolean;
    projectError: Error | null;
  }>();

  const { actions, isSettingsDialogOpen, setSettingsDialogOpen } = useViewerActions(projectId!);

  const href = `/projects/${projectId}`;

  if (!isWebGLSupported) {
    return (
      <div className='max-h-screen overflow-hidden flex flex-col flex-1'>
        <PageSkeleton
          actions={actions}
          steps={projectLoading ? [{ label: 'Loading...', href }] : [{ label: project.name, href }]}>
          <div className='flex flex-col items-center justify-center flex-1'>
            <DangerTriangleSolid className='size-24 text-rose-500' />

            <span className='max-w-96 text-2xl font-semibold mt-12 text-center whitespace-normal'>
              Browser not supported
            </span>
            <span className='max-w-96 text-sm opacity-70 mt-4 text-center whitespace-normal'>
              Please use a modern browser like Chrome, Firefox, or Safari or upgrade your browser to the latest version.
            </span>
            <span className='max-w-96 text-sm opacity-70 mt-2 text-center whitespace-normal'>
              If you believe this is an error, please contact support.
            </span>
          </div>
        </PageSkeleton>
      </div>
    );
  }
  return (
    <div className='max-h-screen overflow-hidden flex flex-col flex-1'>
      <PageSkeleton
        actions={actions}
        steps={projectLoading ? [{ label: 'Loading...', href }] : [{ label: project.name, href }]}>
        <div className='overflow-hidden flex flex-col flex-1'>
          <ErrorBoundary
            key={projectId}
            fallbackRender={({ error }) => (
              <FullPageError
                title='Failed to render 3D scene'
                subtitle='If the issue persists, please contact your administrator.'
                error={error}
              />
            )}>
            <Viewer projectId={projectId!}>
              <FeatureMoveActions />
            </Viewer>
          </ErrorBoundary>
        </div>
      </PageSkeleton>
      {isSettingsDialogOpen && (
        <ViewerSettingsDialog open={isSettingsDialogOpen} onOpenChange={setSettingsDialogOpen} />
      )}
    </div>
  );
};

const FeatureMoveActions: FunctionComponent = () => {
  const [selectedFeature, setSelectedFeature] = useAtom(selectedFeatureAtom);
  const _handleCancel = () => {
    setSelectedFeature(null);
  };
  const _handleSave = () => {
    if (!selectedFeature) return;
    selectedFeature.onSave(selectedFeature);
  };

  if (!selectedFeature?.isMoving) return null;
  return (
    <div className='absolute bottom-4 right-4 z-20 flex gap-2'>
      <Button variant='ghost' className='text-black bg-white/40 backdrop-blur-md' onClick={_handleCancel}>
        Cancel
      </Button>
      <Button variant='default' onClick={_handleSave}>
        <SaveSolid />
        Save
      </Button>
    </div>
  );
};

export default ViewerPage;
